import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Plus, MoreHorizontal, Trash, Edit, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay';
import { EmptyStateAnalytics } from '@/components/analytics/EmptyStateAnalytics';
import DeliverableForm from './DeliverableForm';
import { format } from 'date-fns';
import { useTheme } from '@/contexts/ThemeContext';
import { TwitterDeliverable, TwitterCampaign, TrendDataPoint, KolActivity } from '@/types/twitterTypes';
import ExpandableDeliverable from './ExpandableDeliverable';

interface TwitterCampaignDetailProps {
  campaignId: string;
  onBack: () => void;
}

export const TwitterCampaignDetail: React.FC<TwitterCampaignDetailProps> = ({ campaignId, onBack }) => {
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentDeliverable, setCurrentDeliverable] = useState<TwitterDeliverable | null>(null);
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const { toast } = useToast();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const isLightMode = theme === 'light';
  
  // Fetch campaign details
  const { data: campaign, isLoading: isLoadingCampaign } = useQuery({
    queryKey: ['twitter-campaign', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('twitter_campaigns')
        .select('*')
        .eq('id', campaignId)
        .single();
      
      if (error) throw error;
      return data as TwitterCampaign;
    }
  });
  
  // Fetch deliverables
  const { data: deliverables = [], isLoading: isLoadingDeliverables } = useQuery({
    queryKey: ['twitter-deliverables', campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as TwitterDeliverable[];
    }
  });
  
  // Fetch post trends data with better error handling - we'll keep fetching it for future use
  const { data: postTrendsData = [], refetch: refetchTrends } = useQuery({
    queryKey: ['twitter-post-trends', campaignId],
    queryFn: async () => {
      try {
        console.log("Fetching trend data for campaign:", campaignId);
        const { data, error } = await supabase.rpc('get_campaign_daily_activity', {
          campaign_id: campaignId
        });
        
        if (error) {
          console.error("Error in get_campaign_daily_activity:", error);
          throw error;
        }
        
        console.log("Received trend data:", data);
        
        // Convert the Supabase JSON response to our TrendDataPoint type
        return (data || []).map((item: any) => ({
          date: item.date,
          posts_count: item.posts_count,
          engagements_count: item.engagements_count,
          impressions_count: item.impressions_count,
          kol_data: (typeof item.kol_data === 'string' 
            ? JSON.parse(item.kol_data) 
            : item.kol_data) as KolActivity
        })) as TrendDataPoint[];
      } catch (err) {
        console.error("Error fetching trend data:", err);
        return [];
      }
    }
  });

  // Refresh campaign metrics
  const handleRefreshMetrics = async () => {
    try {
      setRefreshingMetrics(true);
      toast({
        title: 'Refreshing metrics',
        description: 'Updating campaign metrics. This may take a moment...'
      });
      
      console.log("Calling refresh-campaign-metrics for campaign ID:", campaignId);
      
      // Direct call to the refresh-campaign-metrics edge function
      const response = await supabase.functions.invoke('refresh-campaign-metrics', {
        body: { campaignId }
      });
      
      if (response.error) {
        console.error("Error response from refresh-campaign-metrics:", response.error);
        throw new Error(response.error.message || 'Failed to refresh metrics');
      }
      
      console.log("Response from refresh-campaign-metrics:", response.data);
      
      if (!response.data.success) {
        console.error("Failed response from refresh-campaign-metrics:", response.data);
        throw new Error(response.data.error || 'Failed to refresh metrics');
      }
      
      // Refetch campaign details to update the UI
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['twitter-campaign', campaignId] }),
        refetchTrends()
      ]);
      
      toast({
        title: 'Metrics refreshed',
        description: 'Campaign metrics have been updated successfully'
      });
    } catch (err) {
      console.error('Error refreshing metrics:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to refresh campaign metrics',
        variant: 'destructive'
      });
    } finally {
      setRefreshingMetrics(false);
    }
  };
  
  // Create deliverable with enhanced error handling
  const handleCreateDeliverable = async (formData: any) => {
    try {
      console.log("Creating deliverable with data:", formData);
      
      // Check for dollar sign in the query
      if (formData.content_query.includes('$')) {
        toast({
          title: 'Invalid query',
          description: 'Dollar sign ($) is not allowed in the content query',
          variant: 'destructive'
        });
        return;
      }
      
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .insert({
          campaign_id: campaignId,
          name: formData.name,
          content_query: formData.content_query,
          type: formData.type,
          start_date: formData.startDate?.toISOString(),
          end_date: formData.endDate?.toISOString(),
          kol_usernames: formData.kolUsernames || [],
          job_status: 'none',
          processing_progress: 0
        })
        .select();
        
      if (error) {
        console.error("Error creating deliverable:", error);
        throw error;
      }
      
      console.log("Deliverable created successfully:", data);
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['twitter-deliverables'] });
      
      toast({
        title: 'Deliverable created',
        description: `Deliverable "${formData.name}" was created successfully`
      });
      
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error creating deliverable:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create deliverable',
        variant: 'destructive'
      });
    }
  };
  
  // Update deliverable with enhanced error handling
  const handleUpdateDeliverable = async (formData: any) => {
    if (!currentDeliverable) return;
    
    try {
      console.log("Updating deliverable with data:", formData);
      
      // Check for dollar sign in the query
      if (formData.content_query.includes('$')) {
        toast({
          title: 'Invalid query',
          description: 'Dollar sign ($) is not allowed in the content query',
          variant: 'destructive'
        });
        return;
      }
      
      const { data, error } = await supabase
        .from('twitter_deliverables')
        .update({
          name: formData.name,
          content_query: formData.content_query,
          type: formData.type,
          start_date: formData.startDate?.toISOString(),
          end_date: formData.endDate?.toISOString(),
          kol_usernames: formData.kolUsernames || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', currentDeliverable.id)
        .select();
        
      if (error) {
        console.error("Error updating deliverable:", error);
        throw error;
      }
      
      console.log("Deliverable updated successfully:", data);
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['twitter-deliverables'] });
      
      toast({
        title: 'Deliverable updated',
        description: `Deliverable "${formData.name}" was updated successfully`
      });
      
      setIsDialogOpen(false);
      setCurrentDeliverable(null);
    } catch (err) {
      console.error('Error updating deliverable:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to update deliverable',
        variant: 'destructive'
      });
    }
  };
  
  // Delete deliverable
  const handleDeleteDeliverable = async (id: string, name: string) => {
    try {
      const { error } = await supabase
        .from('twitter_deliverables')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: 'Deliverable deleted',
        description: `Deliverable "${name}" was deleted successfully`
      });
    } catch (err) {
      console.error('Error deleting deliverable:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete deliverable',
        variant: 'destructive'
      });
    }
  };
  
  // Handle editing a deliverable
  const handleEditDeliverable = (deliverable: TwitterDeliverable) => {
    setCurrentDeliverable(deliverable);
    setDialogMode('edit');
    setIsDialogOpen(true);
  };
  
  // Handle adding a new deliverable
  const handleAddDeliverable = () => {
    setCurrentDeliverable(null);
    setDialogMode('add');
    setIsDialogOpen(true);
  };
  
  // Handle dialog form submission based on mode
  const handleDialogSubmit = (formData: any) => {
    console.log("Dialog submission with data:", formData, "Mode:", dialogMode);
    if (dialogMode === 'add') {
      handleCreateDeliverable(formData);
    } else {
      handleUpdateDeliverable(formData);
    }
  };
  
  const isLoading = isLoadingCampaign || isLoadingDeliverables;
  
  // Format large numbers with K, M suffix
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };
  
  return (
    <div className="space-y-6">
      {/* Header with back button and campaign name */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="ghost" onClick={onBack} className="p-0 h-8 mr-2">
            <ArrowLeft className="h-4 w-4 mr-1" />
            <span>Back to Campaigns</span>
          </Button>
        </div>
        
        <Button onClick={handleAddDeliverable} className="flex items-center gap-1">
          <Plus className="h-4 w-4" />
          <span>New Deliverable</span>
        </Button>
      </div>
      
      {/* Campaign details */}
      {isLoading ? (
        <Card className={`animate-pulse ${isLightMode ? 'bg-gray-100' : 'bg-[#1a1a1a]'}`}>
          <CardHeader>
            <div className={`h-5 w-3/4 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
            <div className={`h-4 w-1/2 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
          </CardHeader>
          <CardContent>
            <div className={`h-20 ${isLightMode ? 'bg-gray-200' : 'bg-[#2a2a2a]'} rounded`}></div>
          </CardContent>
        </Card>
      ) : campaign ? (
        <>
          <Card className="bg-card border border-border">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{campaign.name}</CardTitle>
                  {campaign.description && (
                    <CardDescription className="mt-1">{campaign.description}</CardDescription>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-muted-foreground flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>Created {format(new Date(campaign.created_at), 'MMM d, yyyy')}</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-8"
                    onClick={handleRefreshMetrics}
                    disabled={refreshingMetrics}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${refreshingMetrics ? 'animate-spin' : ''}`} />
                    <span>Refresh Metrics</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-4 gap-4 pt-6">
              <Card className={`${isLightMode ? 'bg-[#F1F0FB]' : 'bg-muted/20'} shadow-sm border`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Posts</div>
                  <div className="text-2xl font-bold">{formatNumber(campaign.posts_count || 0)}</div>
                </CardContent>
              </Card>
              <Card className={`${isLightMode ? 'bg-[#F1F0FB]' : 'bg-muted/20'} shadow-sm border`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Engagements</div>
                  <div className="text-2xl font-bold">{formatNumber(campaign.engagements_count || 0)}</div>
                </CardContent>
              </Card>
              <Card className={`${isLightMode ? 'bg-[#F1F0FB]' : 'bg-muted/20'} shadow-sm border`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Impressions</div>
                  <div className="text-2xl font-bold">{formatNumber(campaign.impressions_count || 0)}</div>
                </CardContent>
              </Card>
              <Card className={`${isLightMode ? 'bg-[#F1F0FB]' : 'bg-muted/20'} shadow-sm border`}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">Engagement Rate</div>
                  <div className="text-2xl font-bold">{(campaign.engagement_rate || 0).toFixed(2)}%</div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-2">Campaign Not Found</h2>
          <p className="text-muted-foreground mb-6">This campaign might have been deleted or does not exist.</p>
          <Button onClick={onBack}>Return to Campaigns</Button>
        </div>
      )}
      
      {/* Deliverables list */}
      <div className="relative">
        <TableLoadingOverlay isLoading={isLoading} message="Loading deliverables..." />
        
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Deliverables ({deliverables.length})</h2>
            
            {!isLoading && (
              <p className="text-sm text-muted-foreground">
                {deliverables.filter(d => d.job_status === 'completed').length} completed
              </p>
            )}
          </div>
          
          {!isLoading && deliverables.length === 0 ? (
            <EmptyStateAnalytics 
              message="No deliverables found. Create your first deliverable to start fetching tweets."
            />
          ) : (
            <div className="space-y-4">
              {deliverables.map((deliverable, index) => (
                <div key={deliverable.id} className="relative">
                  <div className="absolute left-0 top-0 w-6 h-6 flex items-center justify-center rounded-full bg-muted text-xs font-medium -ml-3 -mt-1">
                    {index + 1}
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="absolute right-2 top-2 h-8 w-8 p-0 z-10"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditDeliverable(deliverable)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        className="text-red-500 cursor-pointer"
                        onClick={() => handleDeleteDeliverable(deliverable.id, deliverable.name)}
                      >
                        <Trash className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <ExpandableDeliverable deliverable={deliverable} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Dialog for adding/editing deliverables */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'add' ? 'Create New Deliverable' : 'Edit Deliverable'}</DialogTitle>
            <DialogDescription>
              Define search parameters to fetch tweets from Twitter
            </DialogDescription>
          </DialogHeader>
          
          <DeliverableForm 
            onSubmit={handleDialogSubmit} 
            onCancel={() => setIsDialogOpen(false)}
            initialData={dialogMode === 'edit' && currentDeliverable ? {
              name: currentDeliverable.name,
              content_query: currentDeliverable.content_query,
              type: currentDeliverable.type,
              startDate: currentDeliverable.start_date ? new Date(currentDeliverable.start_date) : null,
              endDate: currentDeliverable.end_date ? new Date(currentDeliverable.end_date) : null,
              kolUsernames: currentDeliverable.kol_usernames || []
            } : {}}
            isEditing={dialogMode === 'edit'}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TwitterCampaignDetail;
